import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import {
  RuleEngineClient,
  CreateOrUpdateRuleFileRequest,
  RollbackRuleFileRequest,
  EvaluateRuleRequest,
  EvaluateLocalRuleRequest,
} from '../../../infrastructure/RuleEngineClient';

const useRuleEngineClient = () => {
  return new RuleEngineClient(getAdminApiUrl(AdminApiDomain.RULE_ENGINE));
};

export const useRuleEngine = () => {
  const client = useRuleEngineClient();
  const queryClient = useQueryClient();

  const listRuleFilesQuery = useQuery({
    queryKey: ['rule-engine', 'files'],
    queryFn: () => client.listAllRuleFiles(),
  });

  const getRuleFileQuery = (ruleKey: string) =>
    useQuery({
      queryKey: ['rule-engine', 'file', ruleKey],
      queryFn: () => client.getRuleFile(ruleKey),
      enabled: !!ruleKey,
    });

  const listRuleFileVersionsQuery = (ruleKey: string) =>
    useQuery({
      queryKey: ['rule-engine', 'file-versions', ruleKey],
      queryFn: () => client.listRuleFileVersions(ruleKey),
      enabled: !!ruleKey,
    });

  const createOrUpdateRuleFileMutation = useMutation({
    mutationFn: ({ ruleKey, request }: { ruleKey: string; request: CreateOrUpdateRuleFileRequest }) =>
      client.createOrUpdateRuleFile(ruleKey, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'files'] });
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'file', variables.ruleKey] });
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'file-versions', variables.ruleKey] });
    },
  });

  const rollbackRuleFileMutation = useMutation({
    mutationFn: ({ ruleKey, request }: { ruleKey: string; request: RollbackRuleFileRequest }) =>
      client.rollbackRuleFile(ruleKey, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'files'] });
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'file', variables.ruleKey] });
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'file-versions', variables.ruleKey] });
    },
  });

  const evaluateRuleMutation = useMutation({
    mutationFn: ({ ruleKey, request }: { ruleKey: string; request: EvaluateRuleRequest }) =>
      client.evaluateRule(ruleKey, request),
  });

  const evaluateLocalRuleMutation = useMutation({
    mutationFn: (request: EvaluateLocalRuleRequest) => client.evaluateLocalRule(request),
  });

  const reloadRuleMutation = useMutation({
    mutationFn: (ruleKey: string) => client.reloadRule(ruleKey),
    onSuccess: (_, ruleKey) => {
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'file', ruleKey] });
    },
  });

  const deleteRuleFileMutation = useMutation({
    mutationFn: (ruleKey: string) => client.deleteRuleFile(ruleKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rule-engine', 'files'] });
    },
  });

  return {
    listRuleFiles: listRuleFilesQuery.data,
    isLoadingFiles: listRuleFilesQuery.isLoading,
    filesError: listRuleFilesQuery.error,
    refetchFiles: listRuleFilesQuery.refetch,

    getRuleFile: getRuleFileQuery,
    listRuleFileVersions: listRuleFileVersionsQuery,

    createOrUpdateRuleFile: createOrUpdateRuleFileMutation.mutate,
    isCreatingOrUpdatingFile: createOrUpdateRuleFileMutation.isPending,
    createOrUpdateFileError: createOrUpdateRuleFileMutation.error,

    rollbackRuleFile: rollbackRuleFileMutation.mutate,
    isRollingBack: rollbackRuleFileMutation.isPending,
    rollbackError: rollbackRuleFileMutation.error,

    evaluateRule: evaluateRuleMutation.mutate,
    isEvaluating: evaluateRuleMutation.isPending,
    evaluateError: evaluateRuleMutation.error,
    evaluateResult: evaluateRuleMutation.data,

    evaluateLocalRule: evaluateLocalRuleMutation.mutate,
    isEvaluatingLocal: evaluateLocalRuleMutation.isPending,
    evaluateLocalError: evaluateLocalRuleMutation.error,
    evaluateLocalResult: evaluateLocalRuleMutation.data,

    reloadRule: reloadRuleMutation.mutate,
    isReloading: reloadRuleMutation.isPending,
    reloadError: reloadRuleMutation.error,

    deleteRuleFile: deleteRuleFileMutation.mutate,
    isDeleting: deleteRuleFileMutation.isPending,
    deleteError: deleteRuleFileMutation.error,
  };
};
